import { Button, Drawer } from "@etudescarre/ui";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";

type GroupFormData = {
  name: string;
  shortName?: string;
};

export type ContractGroupFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  yardId: number;
  onSuccess?: () => void;
};

/** Creates a groupement; existing ones are edited in the contracts panel's draft. */

export function ContractGroupForm({
  open,
  onOpenChange,
  yardId,
  onSuccess,
}: ContractGroupFormProps) {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<GroupFormData>({
    defaultValues: { name: "", shortName: "" },
  });

  useEffect(() => {
    if (open) reset({ name: "", shortName: "" });
  }, [open, reset]);

  const onSubmit = async (data: GroupFormData) => {
    const shortName = data.shortName?.trim() ? data.shortName.trim() : null;

    try {
      const { error } = await apiClient.POST(
        "/api/admin/yards/contract-groups",
        {
          body: { yardId, name: data.name, shortName },
        },
      );
      if (error) {
        toaster.error({
          title: "Erreur",
          description: error.error || "Échec de la création du groupement",
        });
        return;
      }
      toaster.success({
        title: "Succès",
        description: "Groupement créé avec succès",
      });

      onSuccess?.();
      onOpenChange(false);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    }
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <div className="flex items-center justify-between border-b border-zinc-200 p-6 dark:border-zinc-800">
              <div>
                <Drawer.Title>Créer un groupement</Drawer.Title>
                <Drawer.Description>
                  Créez un nouveau groupement d'entreprises
                </Drawer.Description>
              </div>
              <Drawer.CloseTrigger />
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
                <div>
                  <label
                    htmlFor="group-name"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="group-name"
                    type="text"
                    {...register("name")}
                    required
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Nom du groupement"
                  />
                </div>

                <div>
                  <label
                    htmlFor="group-shortName"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Abréviation
                  </label>
                  <input
                    id="group-shortName"
                    type="text"
                    {...register("shortName")}
                    maxLength={50}
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Ex: GPT-A"
                  />
                </div>
              </div>

              <div className="flex shrink-0 justify-end gap-3 border-t border-zinc-200 p-6 dark:border-zinc-800">
                <Button
                  type="button"
                  outline
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "En cours..." : "Créer"}
                </Button>
              </div>
            </form>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
